const API = (process.env.REACT_APP_RECORDING_API_URL || "http://localhost:4000").replace(/\/$/, "");

export function readSession(roomName) {
  try {
    const raw = sessionStorage.getItem(`biz-rec:${roomName}`);
    if (!raw) return null;
    const session = JSON.parse(raw);
    if (!session?.accessToken || session.roomName !== roomName) return null;
    return session;
  } catch {
    return null;
  }
}

function writeSession(session) {
  sessionStorage.setItem(`biz-rec:${session.roomName}`, JSON.stringify(session));
  if (session.role === "host") {
    sessionStorage.setItem(`biz-host:${session.roomName}`, "1");
  }
}

async function request(path, { token, method = "GET", body } = {}) {
  const res = await fetch(`${API}${path}`, {
    method,
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }
  if (!res.ok) {
    const error = new Error(data?.error || "Recording request failed");
    error.status = res.status;
    throw error;
  }
  return data;
}

export async function openSession({ roomName, displayName, asHost }) {
  const existing = readSession(roomName);
  if (existing && (!asHost || existing.role === "host")) return existing;

  if (asHost) {
    try {
      const session = await request("/api/meetings", {
        method: "POST",
        body: { roomName, displayName },
      });
      writeSession(session);
      return session;
    } catch (err) {
      if (err.status !== 403) throw err;
    }
  }

  const session = await request("/api/meetings/join", {
    method: "POST",
    body: { roomName, displayName },
  });
  writeSession(session);
  return session;
}

export function startRecording(meetingId, token) {
  return request(`/api/meetings/${meetingId}/recording/start`, { method: "POST", token });
}

export function stopRecording(meetingId, token) {
  return request(`/api/meetings/${meetingId}/recording/stop`, { method: "POST", token });
}

export function updateRecordingStatus(recordingId, token, status, errorMessage) {
  return request(`/api/recordings/${recordingId}/status`, {
    method: "POST",
    token,
    body: { status, errorMessage },
  });
}

export function listRecordings(meetingId, token) {
  return request(`/api/meetings/${meetingId}/recordings`, { token });
}

export async function downloadRecording(recordingId, token, fileName, view) {
  const res = await fetch(`${API}/api/recordings/${recordingId}/download`, {
    headers: { authorization: `Bearer ${token}` },
  });
  const type = res.headers.get("content-type") || "";
  if (type.includes("application/json")) {
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Download failed");
    window.open(data.url, "_blank", "noopener,noreferrer");
    return;
  }
  if (!res.ok) throw new Error("Download failed");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  if (view) {
    window.open(url, "_blank", "noopener,noreferrer");
    return;
  }
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName || "recording.mp4";
  link.click();
  URL.revokeObjectURL(url);
}
